import { useState, type ReactNode } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { Box } from "../../components/ui/box";
import type { Omittable } from "../../lib/props";
import { useChartColors, type ChartColorToken } from "./colors";
import { CHART_MOTION, TooltipShell, useChartMotion } from "./internal/recharts";

/** One slice of the composition. */
export type DonutChartSlice = Omittable<{
  id: string;
  label: string;
  value: number;
}>;

export type DonutChartProps = Omittable<{
  data: readonly DonutChartSlice[];
  /** Index-aligned slice colors (`useCategoricalColors(...).tokens`) so color
   * follows the entity, never its sort position. */
  colorTokens: readonly ChartColorToken[];
  /** Hole radius as a fraction of the outer radius; `0` is a solid pie. */
  innerRatio?: number;
  /** Gap between slices, in degrees. */
  padAngle?: number;
  cornerRadius?: number;
  /** One summary value (and caption) centered in the hole — keep it to that. */
  center?: ReactNode;
  onSliceClick?: (slice: DonutChartSlice, index: number) => void;
  /** Hover card content for the hovered slice; omit for no hover card. */
  renderTooltip?: (slice: DonutChartSlice) => ReactNode;
  ariaLabel?: string;
}>;

/**
 * The finished donut/pie chart (recharts engine). Slices start at 12 o'clock
 * and run clockwise; hovering a slice quiets the others a little (handled
 * internally — there is no hover surface to wire). Colors are semantic tokens
 * painted as live `var()` strings; recharts never crosses this surface.
 */
export function DonutChart({
  data,
  colorTokens,
  innerRatio = 0,
  padAngle,
  cornerRadius,
  center,
  onSliceClick,
  renderTooltip,
  ariaLabel,
}: DonutChartProps) {
  const c = useChartColors();
  const animate = useChartMotion();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      role={ariaLabel === undefined ? undefined : "img"}
      aria-label={ariaLabel}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          {renderTooltip ? (
            <Tooltip
              isAnimationActive={false}
              content={({ active, payload }) => {
                const slice = payload?.[0]?.payload as DonutChartSlice | undefined;
                if (!active || slice === undefined) return null;
                return <TooltipShell>{renderTooltip(slice)}</TooltipShell>;
              }}
            />
          ) : null}
          <Pie
            data={data as DonutChartSlice[]}
            dataKey="value"
            nameKey="label"
            // 12 o'clock start, clockwise — recharts' zero is 3 o'clock
            // counterclockwise.
            startAngle={90}
            endAngle={-270}
            innerRadius={`${Math.round(innerRatio * 100)}%`}
            outerRadius="100%"
            stroke="none"
            isAnimationActive={animate}
            {...CHART_MOTION}
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
            {...(padAngle !== undefined ? { paddingAngle: padAngle } : null)}
            {...(cornerRadius !== undefined ? { cornerRadius } : null)}
            {...(onSliceClick
              ? {
                  style: { cursor: "pointer" },
                  onClick: (_: unknown, index: number) => {
                    const slice = data[index];
                    if (slice !== undefined) onSliceClick(slice, index);
                  },
                }
              : null)}
          >
            {data.map((slice, i) => (
              <Cell
                key={slice.id}
                fill={c.paint(colorTokens[i])}
                // A light quiet, not an on/off: enough to lift the hovered
                // slice out, not enough to read as "the rest went inactive".
                // A pie is often one dominant slice, so a hard dim washes the
                // whole chart out to spotlight a sliver.
                fillOpacity={activeIndex === null || activeIndex === i ? 1 : 0.8}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {center !== undefined ? (
        <Box
          position="absolute"
          inset={0}
          direction="column"
          align="center"
          justify="center"
          className="pointer-events-none"
        >
          {center}
        </Box>
      ) : null}
    </Box>
  );
}
